<template>
  <b-modal
    id="generate-report-modal"
    :visible="visible"
    title="Generate Report"
    size="md"
    centered
    @hidden="onHidden"
    @ok="generateReport"
  >
    <div class="generate-report-modal">
      <!-- Success Message -->
      <b-alert v-if="reportGenerated" variant="success" show dismissible @dismissed="reportGenerated = false">
        <i class="fas fa-check-circle mr-2"></i>
        Report generated successfully! Download started.
      </b-alert>

      <!-- Report Type Selection -->
      <b-form-group label="File Type" label-for="report-type">
        <div class="file-type-options">
          <label
            v-for="type in fileTypes"
            :key="type.value"
            class="file-type-option"
            :class="{ selected: fileType === type.value }"
          >
            <input
              type="radio"
              :value="type.value"
              v-model="fileType"
              class="d-none"
            />
            <div class="option-content">
              <i :class="type.icon"></i>
              <span class="type-label">{{ type.label }}</span>
              <span class="type-ext">.{{ type.value }}</span>
            </div>
          </label>
        </div>
      </b-form-group>

      <!-- File Name -->
      <b-form-group label="File Name" label-for="report-filename">
        <b-input-group>
          <b-form-input
            id="report-filename"
            v-model="fileName"
            :placeholder="defaultFileName"
          ></b-form-input>
          <b-input-group-append is-text>
            .{{ fileType }}
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted">Leave blank to use default name</small>
      </b-form-group>

      <!-- Report Options -->
      <b-form-group label="Include in Report">
        <div class="report-options">
          <b-form-checkbox v-model="options.summary" :disabled="true">
            <span>Summary Statistics</span>
            <small class="text-muted d-block">Overall metrics and rates</small>
          </b-form-checkbox>

          <b-form-checkbox v-model="options.timeline">
            <span>Timeline Data</span>
            <small class="text-muted d-block">Daily/weekly performance trends</small>
          </b-form-checkbox>

          <b-form-checkbox v-model="options.campaigns">
            <span>Campaign Details</span>
            <small class="text-muted d-block">Individual campaign metrics</small>
          </b-form-checkbox>

          <b-form-checkbox v-model="options.domains">
            <span>Email Domains</span>
            <small class="text-muted d-block">Distribution by email provider</small>
          </b-form-checkbox>

          <b-form-checkbox v-model="options.bounces">
            <span>Bounce Details</span>
            <small class="text-muted d-block">Hard and soft bounce breakdown</small>
          </b-form-checkbox>
        </div>
      </b-form-group>

      <!-- View Generated Reports Link -->
      <div class="generated-reports-link">
        <a href="#" @click.prevent="$emit('view-reports')">
          <i class="fas fa-folder-open mr-1"></i>
          View generated reports
        </a>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="outline-secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="generating"
        @click="ok()"
      >
        <b-spinner v-if="generating" small class="mr-2"></b-spinner>
        <i v-else class="fas fa-download mr-2"></i>
        Generate Report
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'GenerateReportModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    reportTitle: {
      type: String,
      default: 'Campaign Statistics'
    },
    dateRange: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      fileType: 'pdf',
      fileName: '',
      generating: false,
      reportGenerated: false,
      options: {
        summary: true,
        timeline: true,
        campaigns: true,
        domains: false,
        bounces: false
      },
      fileTypes: [
        { value: 'pdf', label: 'PDF', icon: 'fas fa-file-pdf' },
        { value: 'xlsx', label: 'Excel', icon: 'fas fa-file-excel' },
        { value: 'csv', label: 'CSV', icon: 'fas fa-file-csv' }
      ]
    };
  },

  computed: {
    defaultFileName() {
      const date = new Date().toISOString().split('T')[0];
      return `campaign-statistics-${date}`;
    },

    finalFileName() {
      return this.fileName || this.defaultFileName;
    }
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  },

  methods: {
    resetForm() {
      this.fileType = 'pdf';
      this.fileName = '';
      this.generating = false;
      this.reportGenerated = false;
      this.options = {
        summary: true,
        timeline: true,
        campaigns: true,
        domains: false,
        bounces: false
      };
    },

    async generateReport(event) {
      event.preventDefault();

      this.generating = true;
      this.reportGenerated = false;

      try {
        // Mock API call - in production this would generate actual report
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit event with report configuration
        this.$emit('generate', {
          fileType: this.fileType,
          fileName: this.finalFileName,
          options: this.options,
          dateRange: this.dateRange
        });

        this.reportGenerated = true;

        // Auto-close after success
        setTimeout(() => {
          this.$emit('update:visible', false);
        }, 1500);

      } catch (err) {
        console.error('Error generating report:', err);
      } finally {
        this.generating = false;
      }
    },

    onHidden() {
      this.$emit('update:visible', false);
      this.$emit('hidden');
    }
  }
};
</script>

<style lang="scss" scoped>
.generate-report-modal {
  .file-type-options {
    display: flex;
    gap: 12px;

    .file-type-option {
      flex: 1;
      cursor: pointer;

      .option-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 16px 12px;
        border: 2px solid #dee2e6;
        border-radius: 8px;
        transition: all 0.2s ease;

        i {
          font-size: 24px;
          margin-bottom: 8px;
          color: #6c757d;
        }

        .type-label {
          font-weight: 500;
          color: #212529;
        }

        .type-ext {
          font-size: 12px;
          color: #6c757d;
        }
      }

      &:hover .option-content {
        border-color: #adb5bd;
      }

      &.selected .option-content {
        border-color: #007bff;
        background: #f8f9ff;

        i {
          color: #007bff;
        }
      }
    }
  }

  .report-options {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .custom-control {
      padding: 12px;
      background: #f8f9fa;
      border-radius: 6px;

      &:hover {
        background: #e9ecef;
      }

      span {
        font-weight: 500;
      }
    }
  }

  .generated-reports-link {
    text-align: center;
    padding-top: 16px;
    border-top: 1px solid #dee2e6;

    a {
      color: #007bff;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
